import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  property_address: "",
  service_needed: "",
  message: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.property_address || !form.service_needed) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote-requests`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us at 954-645-2753.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-success"
        className="rounded-2xl bg-white border border-slate-100 shadow-sm p-10 text-center flex flex-col items-center"
      >
        <div className="w-16 h-16 rounded-full bg-lime-100 flex items-center justify-center text-lime-600 mb-5">
          <CheckCircle2 className="h-9 w-9" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">Request Received!</h3>
        <p className="text-slate-600 mb-6">
          Thanks for reaching out to Luigi's Hydro Clean. We'll contact you soon with your free, no-obligation estimate.
        </p>
        <button
          data-testid="quote-reset-btn"
          onClick={() => setDone(false)}
          className="rounded-full border-2 border-slate-900 px-6 py-3 text-sm font-bold text-slate-900 transition-all hover:bg-slate-900 hover:text-white"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="quote-form"
      onSubmit={submit}
      className="rounded-2xl bg-white border border-slate-100 shadow-lg p-6 sm:p-8 space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            data-testid="input-name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="John Smith"
            className="h-11 focus-visible:ring-cyan-500/30 focus-visible:border-cyan-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            data-testid="input-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="954-000-0000"
            className="h-11 focus-visible:ring-cyan-500/30 focus-visible:border-cyan-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            data-testid="input-email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@email.com"
            className="h-11 focus-visible:ring-cyan-500/30 focus-visible:border-cyan-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="address">Property Address *</Label>
          <Input
            id="address"
            data-testid="input-address"
            value={form.property_address}
            onChange={(e) => update("property_address", e.target.value)}
            placeholder="123 Ocean Dr, Fort Lauderdale"
            className="h-11 focus-visible:ring-cyan-500/30 focus-visible:border-cyan-500"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Service Needed *</Label>
        <Select value={form.service_needed} onValueChange={(v) => update("service_needed", v)}>
          <SelectTrigger
            data-testid="select-service"
            className="h-11 focus:ring-cyan-500/30 focus:border-cyan-500"
          >
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt} data-testid={`service-option-${opt}`}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message / Additional Details</Label>
        <Textarea
          id="message"
          data-testid="input-message"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Tell us about your project..."
          rows={4}
          className="focus-visible:ring-cyan-500/30 focus-visible:border-cyan-500"
        />
      </div>

      <button
        type="submit"
        data-testid="submit-quote-btn"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-cyan-500 px-8 py-4 text-base font-bold text-white transition-all hover:bg-cyan-600 hover:shadow-lg disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {loading ? "Sending..." : "Submit Request"}
      </button>
    </form>
  );
};
